import { CompositionNormalizer } from '@src/components/sidepanel/compositionNormalizer/compositionNormalizer';
import { AspectRatio } from '@src/types.ts/video';
import { VideoEditorLogger } from '@src/utility/VideoEditorLogger';
import {
  convertFromPixels,
  convertToPixels,
} from '@src/utility/videoTranscriptionUtils';

import { SceneService } from '../../services/SceneService';
import { FONT_SIZE_VALUES } from '../../utility/general';
import { Element } from '../models';
import { LogoPositioner } from '../models/aspectRatio/LogoPositioner';
import { MediaPositioner } from '../models/aspectRatio/MediaPositioner';
import {
  createDefaultCropKeyframe,
  creatomateToNormalizedCrop,
} from '../models/layout/CropTransformUtils';
import { LayoutTemplateRegistry } from '../models/layout/LayoutTemplateRegistry';
import { buildBindingsForTemplate } from '../models/layout/templateUtils';
import { KARAOKE_TRACK_NUMBER } from '../models/Timeline';
import { AbstractCommand } from './AbstractCommand';
import { ElementTypeTransitionCommand } from './ElementTypeTransitionCommand';
import { UpdateKaraokeConfigCommand } from './karaoke/UpdateKaraokeConfigCommand';
import { UpdateScenesCommand } from './UpdateScenesCommand';

import type {
  CompositionElement,
  ImageElement,
  TextElement,
  VideoElement,
} from '../models';
import type { KaraokeClip } from '../models/karaoke/KaraokeClip';
import type { CropTransformKeyframe, Scene } from '../models/layout';
import type { NaturalDimensions, VideoDimensions } from '../types/aspectRatio';
import type { VideoEditor } from '../VideoEditor';
import type { AspectConfiguration } from '@src/components/sidepanel/CompositionSettings/types';
import type { IKaraokePresetProvider } from '@src/services/karaoke/KaraokePresetProvider';
import type { IKaraokeTemplateVariantService } from '@src/services/karaoke/KaraokeTemplateVariantService';

export interface ChangeAspectRatioCommandParams {
  originalVideoDimensions: VideoDimensions;
  prevVideoDimensions: VideoDimensions;
  newVideoDimensions: VideoDimensions;
  naturalMediaDimensions: Record<
    VideoElement['source'] | ImageElement['source'],
    NaturalDimensions
  >;
  basicTextSetting: Record<'font_size' | 'y' | 'width', string>;
  basicKaraokeSetting: Record<'font_size' | 'y' | 'width', string>;
  // Provider holding presets + variant application logic (reuses TextBrandingService internally)
  karaokePresetProvider?: IKaraokePresetProvider;
  variantService?: IKaraokeTemplateVariantService;
}

export class ChangeAspectRatioCommand extends AbstractCommand {
  private params: ChangeAspectRatioCommandParams;

  private readonly sceneService = new SceneService();

  getName(): string {
    return 'ChangeAspectRatioCommand';
  }

  shouldCreateUndoRedoEntry(): boolean {
    return false;
  }

  private canUndo: boolean = false;

  constructor(editor: VideoEditor, params: ChangeAspectRatioCommandParams) {
    super(editor);
    this.params = params;
  }

  execute(): boolean {
    super.execute();

    if (!this.adjustTimelineDimensions()) return false;

    const success = this.adjustAllElementsToAspectRatio();
    if (!success) return false;

    this.adjustScenesForAspectRatio();

    this.canUndo = true;
    return true;
  }

  undo(): boolean {
    if (!this.canUndo) return false;

    return super.undo();
  }

  getDescription(): string {
    const {
      prevVideoDimensions: prevVideoDimension,
      newVideoDimensions: newVideoDimension,
    } = this.params;
    return `Change aspect ratio from ${prevVideoDimension.aspectRatio} to ${newVideoDimension.aspectRatio}`;
  }

  private adjustTimelineDimensions(): boolean {
    try {
      const { width, height } = this.params.newVideoDimensions;
      this.editor.timeline.setWidth(width);
      this.editor.timeline.setHeight(height);
      return true;
    } catch (error) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Failed to adjust timeline dimensions',
      });
      return false;
    }
  }

  private adjustAllElementsToAspectRatio(): boolean {
    for (const element of this.editor.timeline.allElements) {
      const isVideo = Element.isVideoElement(element);
      const isImage = Element.isImageElement(element);
      const isComposition = Element.isCompositionElement(element);
      const isLogo = Element.isLogoElement(element);
      const isText = Element.isTextElement(element);
      const isKaraokeText = isText && element.track === KARAOKE_TRACK_NUMBER;
      const isCompositionBlackFrame =
        isComposition && this.shouldConvertBlackFrameComposition(element);
      const isDesignComposition =
        isComposition && this.extraElementData.isDesignComposition(element);
      const isFullscreen =
        isComposition && this.extraElementData.isCompositionFullscreen(element);
      // Check if we need to convert black frame compositions to regular images

      if (isDesignComposition && !isFullscreen) {
        element.update(this.getDesignCompositionAdjustments(element));
      } else if (isCompositionBlackFrame) {
        this.convertBlackFrameComposition(element);
      } else if (isLogo) {
        element.update(this.getLogoElementAdjustments(element));
      } else if (isImage) {
        element.update(this.getMediaElementAdjustments(element));
      } else if (isVideo) {
        element.update(MediaPositioner.getDefaultPositioning());
      } else if (isText && !isKaraokeText) {
        element.update(this.getTextElementAdjustments(element));
      }
    }

    this.switchAspectRatiosOfKaraokeElements();

    return true;
  }

  // -------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------

  private getMediaElementAdjustments(
    element: VideoElement | ImageElement,
  ): Record<'x' | 'y' | 'width' | 'height', string> {
    // For elements with fill or contain fit, use default values
    if (element.fit && ['fill', 'contain'].includes(element.fit)) {
      return MediaPositioner.getDefaultPositioning();
    }

    const {
      originalVideoDimensions: originalDimension,
      newVideoDimensions: newDimension,
    } = this.params;

    // Create a media positioner with the dimensions needed for calculations
    const mediaPositioner = new MediaPositioner(
      originalDimension,
      newDimension,
    );

    // Get adjusted dimensions from the positioner
    const naturalDimensions = this.getNaturalDimensionsForElement(element);
    return mediaPositioner.getAdjustedDimensions(naturalDimensions);
  }

  private getLogoElementAdjustments(
    element: ImageElement,
  ): Record<'x' | 'y' | 'width' | 'height', string> {
    const { prevVideoDimensions, newVideoDimensions } = this.params;

    // Create a logo positioner with the required parameters
    const logoPositioner = new LogoPositioner(
      prevVideoDimensions.aspectRatio,
      newVideoDimensions.aspectRatio,
      element,
    );

    // Get source aspect ratio from element's natural dimensions
    let sourceAspectRatio = 1;
    const sourceDimensions = this.getNaturalDimensionsForElement(element);
    if (sourceDimensions) {
      sourceAspectRatio = sourceDimensions.width / sourceDimensions.height;
    }

    // Get adjusted dimensions from the logo positioner
    return logoPositioner.getAdjustedDimensions(sourceAspectRatio);
  }

  private getDesignCompositionAdjustments(
    element: CompositionElement,
  ): Record<'x' | 'y' | 'width' | 'height', string> {
    const { prevVideoDimensions, newVideoDimensions } = this.params;
    const metadata = this.extraElementData.getCompositionMetadata(element) as {
      sixteenByNine?: AspectConfiguration;
      nineBySixteen?: AspectConfiguration;
      oneByOne?: AspectConfiguration;
    };

    // Extract aspect configurations for old and new aspect ratios
    const oldScalingConfiguration =
      CompositionNormalizer.getAspectConfigurationFromMetadata(
        metadata,
        prevVideoDimensions.aspectRatio,
      );
    const newScalingConfiguration =
      CompositionNormalizer.getAspectConfigurationFromMetadata(
        metadata,
        newVideoDimensions.aspectRatio,
      );

    // Parse scale values. Per-axis, so a non-uniformly resized composition keeps
    // its proportions across an aspect-ratio change; legacy configs carrying only
    // a uniform `scale` resolve to equal values on both axes.
    const oldScale = CompositionNormalizer.resolveAxisScales(
      oldScalingConfiguration,
    );
    const newScale = CompositionNormalizer.resolveAxisScales(
      newScalingConfiguration,
    );

    // Calculate new position (scale is ignored for 'position')
    const newX = newScalingConfiguration.positionX
      ? CompositionNormalizer.convertToPercentage(
          newScalingConfiguration.positionX,
          newVideoDimensions.width,
          newScale.scaleX.toString(),
          'position',
        )
      : element.x;

    const newY = newScalingConfiguration.positionY
      ? CompositionNormalizer.convertToPercentage(
          newScalingConfiguration.positionY,
          newVideoDimensions.height,
          newScale.scaleY.toString(),
          'position',
        )
      : element.y;

    // Convert original dimensions to pixels
    const originalWidthInPixels =
      CompositionNormalizer.convertPercentageToPixels(
        element.width,
        prevVideoDimensions.width,
      );
    const originalHeightInPixels =
      CompositionNormalizer.convertPercentageToPixels(
        element.height,
        prevVideoDimensions.height,
      );

    // Adjust dimensions by scale, each axis by its own factor
    const newWidthInPixels = CompositionNormalizer.adjustDimensionByScale(
      originalWidthInPixels,
      oldScale.scaleX,
      newScale.scaleX,
    );
    const newHeightInPixels = CompositionNormalizer.adjustDimensionByScale(
      originalHeightInPixels,
      oldScale.scaleY,
      newScale.scaleY,
    );

    // Convert adjusted dimensions back to percentage
    // Note: Don't pass scale here since adjustDimensionByScale already applied it
    const newWidth = CompositionNormalizer.convertToPercentage(
      newWidthInPixels.toString(),
      newVideoDimensions.width,
      '100%', // Use 100% to avoid double-scaling
      'size',
    );
    const newHeight = CompositionNormalizer.convertToPercentage(
      newHeightInPixels.toString(),
      newVideoDimensions.height,
      '100%', // Use 100% to avoid double-scaling
      'size',
    );

    return {
      x: newX,
      y: newY,
      width: newWidth,
      height: newHeight,
    };
  }

  private getTextElementAdjustments(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    element: TextElement,
  ): Record<string, string> {
    let result = {} as Record<string, string>;

    const { basicTextSetting, newVideoDimensions } = this.params;
    const { width, height } = newVideoDimensions;

    if ('font_size' in basicTextSetting) {
      let value = basicTextSetting['font_size'];
      const vhValue = convertFromPixels(parseInt(value), 'vh', {
        height,
        width,
      });
      value = `${vhValue}vh`;
      result['font_size'] = value;
    }

    if ('y' in basicTextSetting) {
      result['y'] = basicTextSetting['y'];
    }

    if ('width' in basicTextSetting) {
      result['width'] = basicTextSetting['width'];
    }

    return result;
  }

  // Scale karaoke font size using aspect ratio multipliers.
  // Baseline 16:9 = 1, 9:16 = 2/3, 1:1 = 0.8. We derive a scaleFactor = newMultiplier / prevMultiplier
  // then apply it to the pixel value of the existing font size. Result stored as px in new dimensions.
  private scaleKaraokeConfigFont(
    config: KaraokeClip['karaokeConfig'],
  ): KaraokeClip['karaokeConfig'] {
    const {
      originalVideoDimensions,
      prevVideoDimensions,
      newVideoDimensions,
      basicKaraokeSetting,
    } = this.params;
    const raw = String(config.font_size || '');
    if (!raw) return config;

    const numeric = parseFloat(raw);
    if (isNaN(numeric)) return config;
    const units = raw.match(/[a-z]+/)?.[0] || 'px';

    // Convert existing value to pixels relative to original dimensions
    let pixelValue = numeric;
    if (units === 'vh' || units === 'vw') {
      pixelValue = convertToPixels(numeric, units, {
        width: originalVideoDimensions.width,
        height: originalVideoDimensions.height,
      });
    }

    const aspectMultiplier: Record<AspectRatio, number> = {
      [AspectRatio.AR_16_9]: 1,
      [AspectRatio.AR_9_16]: 2 / 3,
      [AspectRatio.AR_1_1]: 0.8,
    };
    const prevFactor = aspectMultiplier[prevVideoDimensions.aspectRatio] ?? 1;
    const newFactor = aspectMultiplier[newVideoDimensions.aspectRatio] ?? 1;
    const scaleFactor = newFactor / prevFactor;

    const scaledPixels = pixelValue * scaleFactor;
    const snappedPixels = this.snapFontSize(scaledPixels);

    const updatedConfig: KaraokeClip['karaokeConfig'] = {
      ...config,
      font_size: `${snappedPixels}`,
    };

    if ('y' in basicKaraokeSetting) {
      updatedConfig['y'] = basicKaraokeSetting['y'];
    }
    if ('width' in basicKaraokeSetting) {
      updatedConfig['width'] = basicKaraokeSetting['width'];
    }

    // Detach from template lineage if present (manual scaling applied)
    delete updatedConfig.parent_template_id;

    return updatedConfig;
  }

  // Snap arbitrary pixel font size to the closest allowed value in FONT_SIZE_VALUES
  private snapFontSize(px: number): number {
    if (!Array.isArray(FONT_SIZE_VALUES) || FONT_SIZE_VALUES.length === 0) {
      return px;
    }
    let closest = FONT_SIZE_VALUES[0];
    let minDiff = Math.abs(px - closest);
    for (const v of FONT_SIZE_VALUES) {
      const diff = Math.abs(px - v);
      if (diff < minDiff || (diff === minDiff && v < closest)) {
        closest = v;
        minDiff = diff;
      }
    }
    return closest;
  }

  private getNaturalDimensionsForElement(
    element: VideoElement | ImageElement,
  ): NaturalDimensions | undefined {
    const dimensions = this.params.naturalMediaDimensions[element.source];
    if (dimensions) {
      return dimensions;
    }

    console.warn(
      `Natural dimensions not available for element source: ${element.source}, elementId: ${element.id}`,
    );

    return undefined;
  }

  /**
   * Converts a Black Frame composition to a regular image element
   */
  private convertBlackFrameComposition(element: CompositionElement): void {
    // Use ElementTypeTransitionCommand to handle the conversion
    const transitionCommand = new ElementTypeTransitionCommand(
      this.editor,
      element.id,
      'image',
      { fit: 'cover', locked: false },
    );

    const success = transitionCommand.execute();
    if (success) {
      // Get the converted element and apply aspect ratio adjustments
      const convertedElement = this.editor.timeline.getElement(element.id);
      if (convertedElement && Element.isImageElement(convertedElement)) {
        convertedElement.update(
          this.getMediaElementAdjustments(convertedElement),
        );
      }
    } else {
      VideoEditorLogger.logCommand(
        this,
        'error',
        `Failed to convert Black Frame composition ${element.id} using ElementTypeTransitionCommand`,
      );
    }
  }

  /**
   * Determines if a Black Frame composition should be converted to a regular image
   * when changing from 16:9 to non-16:9 aspect ratios
   */
  private shouldConvertBlackFrameComposition(
    element: CompositionElement,
  ): boolean {
    const { prevVideoDimensions, newVideoDimensions } = this.params;

    // Only convert if we're changing FROM 16:9 TO non-16:9
    if (prevVideoDimensions.aspectRatio !== AspectRatio.AR_16_9) {
      return false;
    }

    if (newVideoDimensions.aspectRatio === AspectRatio.AR_16_9) {
      return false;
    }

    // Check if this composition looks like a Black Frame composition
    return element.isBlackFrameComposition();
  }

  private switchAspectRatiosOfKaraokeElements(): void {
    const {
      newVideoDimensions,
      prevVideoDimensions,
      karaokePresetProvider,
      variantService,
    } = this.params;
    const targetAspect = newVideoDimensions.aspectRatio;
    const sourceAspect = prevVideoDimensions.aspectRatio;
    const configsMap = this.editor.getKaraokeConfigsMap();

    // Track which config ids we've updated to avoid duplicate work for
    // multiple clips referencing same config
    const processed: Set<string> = new Set();

    for (const config of configsMap.values()) {
      const configId = config.parent_template_id ?? config.id;
      if (processed.has(configId)) continue;

      let updatedConfig: typeof config | null = null;

      const hasParent = !!config.parent_template_id;
      if (hasParent && karaokePresetProvider) {
        // Attempt to find variant (preset or user template) for target aspect ratio
        const variant = karaokePresetProvider.applyVariant(
          config,
          targetAspect,
        );
        if (variant) {
          // Variant exists, use it
          updatedConfig = variant; // keep parent_template_id linkage
        } else if (variantService) {
          // NO VARIANT EXISTS - scale the config and track for later template creation
          updatedConfig = this.scaleKaraokeConfigFont(config);

          variantService.trackModification(config.id, updatedConfig, {
            sourceConfig: config,
            sourceAspectRatio: sourceAspect,
            targetAspectRatio: targetAspect,
            modificationType: 'scaled',
          });
        }
      } else if (!hasParent) {
        // No parent: scale
        updatedConfig = this.scaleKaraokeConfigFont(config);
      }

      if (updatedConfig && updatedConfig !== config) {
        const cmd = new UpdateKaraokeConfigCommand(this.editor, updatedConfig);
        cmd.execute();
      }

      processed.add(configId);
    }
  }

  /**
   * For elements controlled by scenes, the scene's bindings define the transformation.
   */
  private adjustScenesForAspectRatio(): void {
    const scenes = this.editor.timeline.scenes;
    const registry = LayoutTemplateRegistry.getInstance();
    const updatedScenes = scenes.map(scene => {
      const layout = scene.layout;

      const template = registry.getTemplate(layout.layoutTemplateId);
      if (!template) {
        return null; // Skip scenes with unknown templates
      }

      const normalizedCrop = this.computeNormalizedCrop(scene);

      const bindings = buildBindingsForTemplate(
        template,
        layout.bindings,
        this.editor.getDefaultKaraokeConfig(),
        () => {
          return [
            createDefaultCropKeyframe({
              crop: normalizedCrop,
            }),
          ];
        },
      );
      if (!bindings) {
        return null;
      }

      return {
        sceneId: scene.id,
        updates: {
          layout: {
            id: layout.id,
            layoutTemplateId: layout.layoutTemplateId,
            bindings,
          },
        },
      };
    });

    // Filter out null entries and apply updates
    const validUpdates = updatedScenes.filter(
      (update): update is NonNullable<typeof update> => update !== null,
    );

    if (validUpdates.length > 0) {
      const updateCommand = new UpdateScenesCommand(this.editor, validUpdates);
      updateCommand.execute();
    }
  }

  private computeNormalizedCrop(scene: Scene): CropTransformKeyframe['crop'] {
    for (const [slotName, binding] of Object.entries(scene.layout.bindings)) {
      if (slotName === 'karaoke') {
        continue;
      }
      const elementsInScene = this.sceneService.findElementsInScene(
        this.editor.timeline,
        scene,
        binding.trackId,
      );
      const element = elementsInScene.find((el): el is VideoElement =>
        Element.isVideoElement(el),
      );
      if (element) {
        const adjustments = this.getMediaElementAdjustments(element);
        const xPercent = parseFloat(adjustments.x);
        const yPercent = parseFloat(adjustments.y);
        const widthPercent = parseFloat(adjustments.width);
        const heightPercent = parseFloat(adjustments.height);
        return creatomateToNormalizedCrop(
          xPercent,
          yPercent,
          widthPercent,
          heightPercent,
        );
      }
    }
    // Fallback: return full frame crop if no media element found
    return { x: 0, y: 0, w: 1, h: 1 };
  }
}
